<template>
  <div class="">
    <div class="map-container">
      <MapControls/>
      <div id="weather-legend-container"></div>
      <div class="map-content">
        <div id='map' ref="mapContainer" class="mapbox m-0 p-0"></div>
        <div v-if="miniCoordsBar" class="coordsbar">
          <span class="material-icons">my_location</span> {{ miniCoordsBar }}
        </div>
        <TrailOverviewMapMeasurementPanel v-if="measuredGeoJson" />
        <MapButtons :buttons="buttons" :button-clicked="buttonClicked" />

        <div class="chart-div p-0 m-0" @mouseleave="hover = false" @mouseenter="hover = true"
             v-if="trail"> <TrailOverviewMapChartTopBar />
          <TrailOverviewMapLineChart />
        </div>
      </div>
      <BaseMapStylesDialog />
      <LoadingDialog :show-loading-dialog="loading" loadingWhat="trail data"/>
      <TrailDataCard />
      <Loader v-if="miniLoader" />
      <MeasurementToolHintDialog />
      <WaypointDialog/>
      <WaypointAddingNotification />
      <MapDataPOIDetailDialog/>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch, Ref, onBeforeUnmount } from 'vue'; // Import onBeforeUnmount
import {mapState, useStore} from 'vuex'; // Use useStore hook for Vuex 4+
import mapboxgl from 'mapbox-gl';

// Import components
import LoadingDialog from '../../utils/shared_components/LoadingDialog.vue';
import TrailOverviewMapChartTopBar from './components/TrailOverviewMapChartTopBar.vue';
import TrailOverviewMapLineChart from './components/TrailOverviewMapLineChart.vue';
import BaseMapStylesDialog from ".././base_map_dialog/BaseMapStylesDialog.vue";
import MeasurementToolHintDialog from "../measurement_tool_hint_dialog/MeasurementToolHintDialog.vue";
import TrailOverviewMapMeasurementPanel from "./components/TrailOverviewMapMeasurementPanel.vue";
import MapButtons, { MapButton } from "../components/MapButtons.vue"; // Assuming MapButton type export
import MapControls from "../components/MapControls.vue";
import WaypointDialog from "../waypoint_dialog/WaypointDialog.vue";
import WaypointAddingNotification from "../waypoint_adding_mode_notificaiton/WaypointAddingNotification.vue";
import TrailDataCard from "../trail_data_card/TrailDataCard.vue";
import Loader from "../../utils/shared_components/Loader.vue";
import MapDataPOIDetailDialog from "../map_data_poi_dialog/MapDataPOIDetailDialog.vue"; // Assuming correct path

// Import Utilities
import { HiikerMapStyle } from "../../utils/models/MapStyles"; // Assuming correct path
import { RevenueCatWrapper } from "../../utils/RevenueCatWrapper"; // Assuming correct path
import createGeoLocate from "../../utils/helpers/GeoLocateHelper"; // Assuming correct path
import { ThreeDimensionalMapManager } from "../../utils/models/MapStyles"; // Assuming correct path
import { OnBoardingGoal } from "../../utils/models/OnBoardingGoal"; // Assuming correct path
import emitter from "../../utils/bus";
import { Modal } from "bootstrap";
import {getElevation} from "../../utils/crosshair_altitude_service/pixel_methods";
import {IndexedDBCache} from "../../utils/crosshair_altitude_service/indexed_db_cache";
import {GridRefUtils} from "../../utils/helpers/GridRefPreference";
import {MeasurementPrefUtility} from "../../utils/helpers/MeasurementPref";
import {LayerRegistry} from "../../utils/layer-registry";

// Declare jQuery globally if used for modals - consider replacing modals
// declare var $: any; // Commented out, recommend using Bootstrap Modals via JS

export default defineComponent({
  name: 'TrailOverviewMap',
  components: {
    Loader,
    TrailDataCard,
    WaypointAddingNotification, WaypointDialog,
    MapControls,
    MapButtons,
    TrailOverviewMapMeasurementPanel,
    LoadingDialog,
    TrailOverviewMapLineChart,
    TrailOverviewMapChartTopBar,
    BaseMapStylesDialog,
    MeasurementToolHintDialog,
    MapDataPOIDetailDialog
  },
  data() {
    // Labels are getters so they follow the active locale; `enabled` stays plain mutable state.
    const t = (key: string) => this.$t(key);
    return {
      buttons: [
        { id: 7, get name() { return t('shared.download'); }, icon: 'download', enabled: true, dropIcon: false, primary: true },
        { id: 0, get name() { return t('shared.maps'); }, icon: 'layers', enabled: false, dropIcon: true },
        { id: 1, get name() { return t('trail_overview_map.3d_mode'); }, icon: 'landscape', enabled: true, dropIcon: false },
        { id: 2, get name() { return t('shared.3d_flyover'); }, icon: 'flight', enabled: true, dropIcon: false },
        { id: 3, get name() { return t('trail_overview_map.waypoints'); }, icon: 'location_on', enabled: false, dropIcon: false },
        { id: 4, get name() { return t('trail_overview_map.quick_measure'); }, icon: 'straighten', enabled: this.measurementMode, dropIcon: false },
        { id: 5, get name() { return t('shared.print_map'); }, icon: 'print', enabled: false, dropIcon: false },
        { id: 6, get name() { return t('shared.clear'); }, icon: 'close', enabled: true, dropIcon: false, clear: true },
      ],
      is3dEnabled: false
    }
  },
  computed: {
    ...mapState('trailOverviewMap', ['measuredGeoJson', 'miniCoordsBar', 'loading', 'measurementMode', 'trailId', 'trail']),
    ...mapState('mapDataOverlays', ['miniLoader']),
  },
  watch: {
    is3dEnabled: {
      handler(newVal) {
        const button3d = this.buttons.find(b => b.id === 1); // Ensure ID matches 3D Mode button
        if (button3d) {
          button3d.enabled = this.is3dEnabled; // Update the button's enabled state
        }
      },
    },
    measurementMode: {
      handler(newVal) {
        const measureButton = this.buttons.find(b => b.id === 4); // Quick Measure button
        if (measureButton) {
          measureButton.enabled = newVal;
        }
      },
      immediate: true, // Run immediately on component mount
    }
  },

  methods: {
    getUser() {
      // Access global user object (consider passing via props or getting from store)
      return (window.user && window.user.firebase_user_id) ? window.user : null;
    },
    async buttonClicked(id: number) {
      const currentUser = this.getUser();
      const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus(); // Ensure this utility is accessible

      switch (id) {
        case 0: // Change Map
          if (currentUser) {
            const mapStylesDialog = document.getElementById('map-styles-dialog');
            if (mapStylesDialog) {
              const modal = new Modal(mapStylesDialog);
              modal.show();
            } else {
              console.error("Map styles dialog element not found.");
            }
          } else {
            window.onBoardingVue?.launchFor(OnBoardingGoal.MapStyles);
          }
          break;
        case 1: // Toggle 3D Mode
          // Ensure toggle3DMode updates the state which is3dEnabled computes from or is watched
          await ThreeDimensionalMapManager.toggle3DMode();
          // Manually update ref if manager doesn't provide reactive state
          this.is3dEnabled = ThreeDimensionalMapManager.is3DModeEnabled();
          // Button state update handled by the watcher now
          break;
        case 2: // 3D Flythrough
          // Assuming showMapModalForTrail is a global function/method defined elsewhere
          if (window.showMapModalForTrail && this.trailId) {
            window.showMapModalForTrail(this.trailId);
          } else {
            console.warn("showMapModalForTrail function or trailId not available.");
          }
          break;
        case 3: // Waypoints
          if (currentUser) {
            const element = document.getElementById('waypoint-options-dialog');
            if (element) {
              const modal = new Modal(element);
              modal.show();
            } else {
              console.error("Waypoint options dialog element not found.");
            }
          } else {
            window.onBoardingVue?.launchFor(OnBoardingGoal.Waypoints);
          }
          break;
        case 4: // Quick Measure
          if (isProOrProPlus) {
            if (this.measurementMode) {
              this.$store.commit('trailOverviewMap/stopMeasurementMode');
            } else {
              this.$store.commit('trailOverviewMap/startMeasurementMode');
              const element = document.getElementById('measurement-tool-hint-dialog');
              if (element) {
                const modal = new Modal(element);
                modal.show();
              } else {
                console.error("Measurement tool hint dialog element not found.");
              }
            }
          } else {
            window.location.href = "/purchases"; // Or trigger upgrade modal
          }
          break;
        case 5: // Print Map
          // Assuming printingUrl is defined globally or accessible via store/computed
          if (window.printingUrl) {
            window.location.href = window.printingUrl; // Consider Vue Router
          } else {
            console.warn("window.printingUrl not defined.");
          }
          break;
        case 6: // Clear
          // Dispatch actions or commit mutations to clear relevant state
          this.$store.dispatch('mapDataPOIs/clearAllPoi'); // Assuming this action exists
          if (this.measurementMode) {
            this.$store.commit('trailOverviewMap/stopMeasurementMode');
          }
          // Add any other clearing logic needed (e.g., clearing measurement line if not stopped)
          break;
        case 7: // Download
          // Open the download modal
          if (window.openDownloadModal) {
            window.openDownloadModal();
          } else {
            console.warn("openDownloadModal function not available.");
          }
          break;

        // Handle other button IDs if necessary
      }
    },
  },

  async mounted() {
    this.is3dEnabled = ThreeDimensionalMapManager.is3DModeEnabled()
    this.$store.dispatch('trailOverviewMap/setLoading', true);

    try {
      mapboxgl.accessToken = "pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w"; // Replace with your token or get from env/config
      const initialMapSettings = await this.$store.getters['trailOverviewMap/initialMapSettings']; // Get settings from store

      const map = new mapboxgl.Map({
        ...initialMapSettings // Spread the rest of the settings (center, zoom, style, etc.)
      });
      window.map = map; // Assign to global scope if needed
      window.layerRegistry = new LayerRegistry(map);

      // --- Map Event Listeners ---
      map.on('load', () => {
        console.log('TrailOverviewMap: Mapbox map loaded.');
        emitter.emit('map-loaded'); // Emit map loaded event
        // Ensure createGeoLocate is compatible and handles the map instance correctly
        createGeoLocate(); // Pass map instance if helper needs it
      });

      // Listen for style load event to re-add any necessary layers
      map.on('style.load', async () => {
        emitter.emit('map-style-loaded'); // Emit style loaded event
      });
      emitter.on('map-style-loaded', async () => {
        try {
          await this.$store.dispatch('trailOverviewMap/mapInitialised'); // Fetch map data
          await this.$store.dispatch('trailOverviewMap/getTrail');
          await this.$store.dispatch('trailOverviewMap/updateMeasurementLine');
          await this.$store.dispatch('waypoints/initialiseStore'); // Fetch POIs
        } catch (e) {
          console.error("TrailOverviewMap: Error during style.load data fetch/update:", e);
        }
      });

      // Add other map listeners (moveend, mousemove, click, etc.) as needed for this map view
      // Example: Mousemove for coordsbar
      const handleMouseMove = async (e: mapboxgl.MapMouseEvent & Object) => {
        // Implement or dispatch action to update miniCoordsBar state
        // Example using GridRefUtils and getElevation (ensure these are compatible/accessible)
        const coords = GridRefUtils.preference().convert(e.lngLat);
        const elevation = await getElevation(e.lngLat, e.target.getZoom(), new IndexedDBCache()); // Needs cache/context
        this.$store.commit('trailOverviewMap/setMiniCoordsBar', `${coords.toString()} ${elevation}`);
      };
      map.on('mousemove', handleMouseMove); // Consider throttling this


    } catch (error) {
      console.error("TrailOverviewMap: Failed to initialize map:", error);
      // Handle initialization error
    } finally {
      this.$store.dispatch('trailOverviewMap/setLoading', false); // Ensure loading stops
    }
  },
  beforeUnmount() {
    console.log('TrailOverviewMap: Unsubscribed from weather legend events.');

    // Clean up map instance if created by this component and assigned to window.map
    if (window.map) {
      console.log("TrailOverviewMap: Removing map instance...");
      window.map.remove(); // Clean up Mapbox GL JS instance
      window.map = undefined; // Remove global reference
    }
    console.log("TrailOverviewMap: Cleanup complete.");
  }
});
</script>

<style scoped>
/* Styles remain largely the same */
.map-container {
  flex: 1 1 auto;
  max-width: 100%;
  z-index: 7;
  height: 85svh;
}

.map-content {
  width: 100%;
  height: 100%;
  position: relative;
}

#map {
  width: 100%;
  height: 100%;
}

.backButton {
  background-color: #202020bb;
  top: 20px;
  left: 5%;
  border-radius: 4px;
  z-index: 100;
}

.chart-div {
  bottom: 0;
  width: 100%;
  height: auto; /* Or a specific height */
  position: absolute;
  z-index: 100;
  background-color: rgb(0 0 0 / 74%);
}

.mapboxgl-ctrl-top-left {
  left: 0;
  top: unset; /* Reset top */
  bottom: 0;
}

.mapboxgl-ctrl-bottom-left {
  /* Ensure this doesn't conflict with other elements */
  bottom: 150px !important; /* Keep override if necessary */
  left: 0 !important; /* Keep override if necessary */
}

/* Adjust Mapbox control positioning if needed */
.mapboxgl-ctrl {
  /* May need to adjust default margin/padding */
}

#weather-legend-container{
  position: absolute;
  bottom: 205px; /* Adjust position as needed */
  left: 20px;  /* Adjust position as needed */
  z-index: 200;
  max-width: 76%; /* Limit width */
  width: 310px; /* Or a fixed width */
  pointer-events: none; /* Allow clicks/interactions to pass through */
}
#weather-legend-container img{
  max-width: 100%; /* Ensure image scales within container */
  height: auto; /* Maintain aspect ratio */
  display: block; /* Prevent extra space below image */
}
</style>

<style>
/* Styles remain largely the same */
.mapboxgl-ctrl {
  /* Default Mapbox controls hidden? Ensure necessary controls are added manually or via options */
  /* display: none; */
}

.map-container {
  flex: 1 1 auto;
  max-width: 100%;
  z-index: 7;
}

.map-content {
  width: 100%;
  height: 100%;
  position: relative;
}

.backButton {
  background-color: #202020bb;
  top: 20px;
  left: 5%;
  border-radius: 4px;
  z-index: 100;
}

.chart-div {
  bottom: 0;
  width: 100%;
  height: auto;
  position: absolute;
  z-index: 100;
  background-color: rgb(0 0 0 / 74%);
}

.button-container {
  width: fit-content;
  top: 20px;
  right: 2%;
}

.mapboxgl-ctrl-top-left {
  left: 0;
  top: unset;
  bottom: 0;
}

.btn-success {
  color: #fff;
}

#weather-legend-container{
  position: absolute;
  bottom: 55px;
  left: 20px;
  z-index: 20000000;
  /* opacity: 1; */
  max-width: 76%;
  width: 310px;
}
#weather-legend-container img{
  max-width: 100%;
}

@media (max-width: 900px) {
  .map-title-trail{
    top: unset;
  }

  .button-container {
    width: 100%;
    left: 0;
    right: unset;
  }

  .backButton {
    left: 14%;
  }
}

@media (max-width: 768px) {
  .map-container{
    height: 90svh !important;
  }
}

@media (max-width: 420px) {
  .button-container {
    top: 0;
  }
}

/* Adjust Mapbox control positioning if needed */
.mapboxgl-ctrl-bottom-left {
  bottom: 150px !important; /* Keep override if necessary */
}
</style>
